/**
 * useGsap — GSAP composable with automatic context cleanup.
 *
 * Usage:
 *   const root = ref<HTMLElement | null>(null);
 *   const { gsap, ScrollTrigger, SplitText, gsapContext } = useGsap(root);
 *
 *   onMounted(() => {
 *     gsapContext(() => {
 *       const split = new SplitText('.headline', { type: 'chars' });
 *       gsap.from(split.chars, { opacity: 0, y: 60, stagger: 0.04 });
 *     });
 *   });
 *
 * Passing `scope` scopes string CSS selectors inside `gsapContext` to that
 * element — avoids accidentally targeting elements from other components.
 *
 * NOTE: SplitText modifies the DOM (wraps chars/words/lines in spans).
 * `ctx.revert()` cleans up GSAP tweens but NOT the SplitText DOM changes.
 * Call `split.revert()` manually or use `onBeforeUnmount` to clean up splits.
 */
export const useGsap = (scope?: Ref<HTMLElement | SVGElement | null>) => {
  const { $gsap, $ScrollTrigger, $Flip, $SplitText } = useNuxtApp();

  let ctx: gsap.Context | undefined;

  onMounted(() => {
    ctx = $gsap.context(() => {}, scope?.value ?? document.documentElement);
  });

  onBeforeUnmount(() => {
    ctx?.revert();
  });

  /**
   * Run GSAP code inside the managed context.
   * Every tween, timeline, and ScrollTrigger created inside `fn` is
   * tracked and cleaned up automatically on unmount.
   *
   * Must be called from `onMounted` or later (not during `setup`).
   */
  const gsapContext = (fn: () => void): void => {
    if (!ctx) {
      if (import.meta.dev) {
        console.warn('[useGsap] gsapContext() called before onMounted — animations will not be cleaned up.');
      }
      fn();
      return;
    }
    ctx.add(fn);
  };

  return {
    gsap: $gsap,
    ScrollTrigger: $ScrollTrigger,
    Flip: $Flip,
    SplitText: $SplitText,
    gsapContext,
  };
};
